import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { resolveUserRole } from '../utils/resolveUserRole';

const AdminSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      console.log('🔐 Admin sign in attempt:', { email: formData.email });
      
      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) {
        console.error('❌ Admin sign in error:', error);
        setErrors({ general: error.message || 'Invalid email or password' });
        return;
      }

      console.log('✅ Admin sign in successful:', data);

      localStorage.setItem('user', JSON.stringify({
        id: data.user.id,
        email: data.user.email,
        full_name: data.user.user_metadata?.full_name || ''
      }));

      const { redirectTo } = await resolveUserRole(data.user.id);
      navigate(redirectTo);

    } catch (err) {
      console.error('❌ Unexpected error during sign in:', err);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/admin/forgot-password');
  };

  const handleSignUp = () => {
    navigate('/admin/signup');
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Admin Sign In</h1>
        <p className="text-neutral-600">
          Sign in to manage your EsuSol groups
        </p>
      </div>

      {/* Success Message */}
      {message && (
        <div className="max-w-md mx-auto mb-6 px-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-3">
            <p className="text-sm text-success-800">{message}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                    Email <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.email ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="admin@example.com"
                    autoComplete="email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-error-600">{errors.email}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
                    Password <span className="text-error-500">*</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.password ? 'border-error-500' : 'border-neutral-300'
                    }`}
                    placeholder="•••••••"
                    autoComplete="current-password"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-600">{errors.password}</p>
                  )}
                </div>

                {/* General Error */}
                {errors.general && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <p className="text-sm text-error-800">{errors.general}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In →'}
                </Button>
              </form>

              {/* Links */}
              <div className="mt-6 space-y-3">
                <div className="text-center">
                  <a 
                    href="/admin/forgot-password" 
                    onClick={(e) => {
                      e.preventDefault();
                      handleForgotPassword();
                    }}
                    className="text-primary-600 hover:text-primary-500 text-sm font-medium"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="text-center">
                  <p className="text-sm text-neutral-600">
                    Don't have an account?{' '}
                    <a 
                      href="/admin/signup" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleSignUp();
                      }}
                      className="text-primary-600 hover:text-primary-500 font-medium"
                    >
                      Sign up
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500">
          © 2026 EsuSol. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminSignIn;
